import { Box, FlexProps, Text } from '@chakra-ui/react';
import { useState } from 'react';

import { ResponsiveFlex } from '../Layout';
import { CheckMarkIcon } from './CheckMarkIcon';

type SelectOptionData = {
  id: string;
  title: string;
  description: string;
};

type OptionProps = {
  isSelected: boolean;
  onSelect: (id: string) => void;
} & SelectOptionData;

type LargeOptionSelectProps = Omit<FlexProps, 'onSelect'> & {
  options: SelectOptionData[];
  selected?: string;
  onSelect?: (id: string) => void;
};

const SelectOption = ({ isSelected, onSelect, id, title, description }: OptionProps) => {
  const titleColor = isSelected ? 'black' : 'gray.4';

  const borderStyles = {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: `${isSelected ? 'black' : 'gray.2'}`,
    _hover: {
      borderColor: 'black',
      transition: '0.4s ease-in-out',
    },
  };

  const backgroundColor = isSelected ? 'gray.1' : 'transparent';

  return (
    <Box
      flex="1"
      borderRadius={'lg'}
      bgColor={backgroundColor}
      padding="space.20"
      position="relative"
      cursor="pointer"
      {...borderStyles}
      onClick={() => onSelect(id)}
    >
      {isSelected && (
        <Box position="absolute" right="space.12" top="space.12">
          <CheckMarkIcon />
        </Box>
      )}
      <Text fontSize="lg" fontWeight={'medium'} color={titleColor}>
        {title}
      </Text>
      <Text color="gray.4" mt="space.12">
        {description}
      </Text>
    </Box>
  );
};

export const LargeOptionSelect = ({
  selected,
  options,
  onSelect,
  ...flexProps
}: LargeOptionSelectProps) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(selected);

  return (
    <ResponsiveFlex gap="space.20" {...flexProps}>
      {options.map(data => (
        <SelectOption
          key={data.id}
          isSelected={selectedId === data.id}
          onSelect={(id: string) => {
            setSelectedId(id);
            if (onSelect) onSelect(id);
          }}
          {...data}
        />
      ))}
    </ResponsiveFlex>
  );
};
